import { FunctionComponent, RefObject, useEffect, useRef, useState } from 'react';

import { initWebGL } from './initWebGL';

import styles from './AmbientLight.module.css';

export interface AmbientLightProps {
  videoRef: RefObject<HTMLVideoElement | null>;
  posterRef: RefObject<HTMLImageElement>;
  isVideoPlayingRef: RefObject<boolean>;
}

export const AmbientLight: FunctionComponent<AmbientLightProps> = ({ videoRef, posterRef, isVideoPlayingRef }) => {
  const blurRef = useRef<HTMLCanvasElement>(null);
  const [width, setWidth] = useState<number>(window.innerWidth);
  const [height, setHeight] = useState<number>(window.innerHeight);
  const loadedRef = useRef<boolean>(false);

  useEffect(() => {
    if (!blurRef.current || !posterRef.current) return;
    const video = videoRef.current;
    const poster = posterRef.current;
    const blurCanvas = blurRef.current;

    let step: number | undefined;
    const draw = initWebGL(blurCanvas, video, poster);
    if (!draw) return;

    const drawLoop = (): void => {
      draw(!!isVideoPlayingRef?.current && !!loadedRef?.current);
      step = window.requestAnimationFrame(drawLoop);
    };
    drawLoop();

    const drawPause = (): void => {
      if (step) {
        window.cancelAnimationFrame(step);
        step = undefined;
      }
    };

    const handleStartedData = (): void => {
      loadedRef.current = true;
    };
    const handleEndedData = (): void => {
      loadedRef.current = false;
    };

    const init = (): void => {
      video?.addEventListener('loadeddata', handleStartedData, false);
      video?.addEventListener('playing', handleStartedData, false);
      video?.addEventListener('play', handleStartedData, false);

      video?.addEventListener('ended', handleEndedData, false);
      video?.addEventListener('stalled', handleEndedData, false);
      video?.addEventListener('waiting', handleEndedData, false);
    };

    const cleanup = (): void => {
      video?.removeEventListener('loadeddata', handleStartedData);
      video?.removeEventListener('playing', handleStartedData);
      video?.removeEventListener('play', handleStartedData);

      video?.removeEventListener('ended', handleEndedData);
      video?.removeEventListener('stalled', handleEndedData);
      video?.removeEventListener('waiting', handleEndedData, false);
    };

    init();

    return () => {
      drawPause();
      cleanup();
      loadedRef.current = false;
    };
  }, [videoRef, posterRef, isVideoPlayingRef]);

  useEffect(() => {
    const onResize = (): void => {
      setWidth(window.innerWidth);
      setHeight(window.innerHeight);
    };

    window.addEventListener('resize', onResize);

    return () => {
      window.removeEventListener('resize', onResize);
    };
  }, []);

  return (
    <canvas
      width={width / 15}
      height={height / 15}
      style={{ width, height }}
      aria-hidden="true"
      ref={blurRef}
      className={styles.ambientLight}
    />
  );
};
